import React, { useState, useEffect } from 'react';
import { Container, Button, Spinner } from 'react-bootstrap';
import { ArrowRight } from 'lucide-react';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";
import { fetchPublicRoleListApi } from '../services/api';
import ModernAuditionCard from './ModernAuditionCard';
import './css/AuditionCategoryRow.css';

const AuditionCategoryRow = () => {
  const [dynamicAuditions, setDynamicAuditions] = useState([]);
  const [displayList, setDisplayList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');

  // ✅ UPDATED SLIDER SETTINGS FOR SMOOTH TOUCH/DRAG
  const sliderSettings = {
    dots: false,          // Hide dots at the bottom
    infinite: false,      // Important: Keeps the slider locked to the start/end
    speed: 500,           // Animation speed for auto-snapping
    slidesToShow: 6.5,    // Show 6.5 cards on large desktops
    slidesToScroll: 1,    // Scroll one card at a time
    arrows: false,        // Hide the default prev/next arrows

    // --- KEY PROPERTIES FOR TOUCH & MOUSE DRAGGING ---
    swipeToSlide: true,   // Allows user to drag a slide and it will snap to the next one
    draggable: true,      // Enables mouse dragging on desktop
    touchMove: true,      // Enables touch dragging on mobile (redundant with swipeToSlide but good to have)
    
    // This allows for a more "free" feel, not forcing a full slide scroll on every drag
    variableWidth: false, // Keep this false to maintain consistent card widths

    // Responsive settings to adjust number of slides on different screen sizes
    responsive: [
      { breakpoint: 1600, settings: { slidesToShow: 5.5 } },
      { breakpoint: 1400, settings: { slidesToShow: 4.5 } },
      { breakpoint: 1200, settings: { slidesToShow: 3.5 } },
      { breakpoint: 992,  settings: { slidesToShow: 3.2 } },
      { breakpoint: 768,  settings: { slidesToShow: 2.5 } },
      { breakpoint: 576,  settings: { slidesToShow: 2.1 } },
      { breakpoint: 480,  settings: { slidesToShow: 1.5 } }
    ]
  };

  useEffect(() => {
    const loadTrendingRoles = async () => {
      try {
        const { data } = await fetchPublicRoleListApi();
        // Limit to 12 items for a good slider experience
        const fullData = data.data?.slice(0, 12) || []; 
        setDynamicAuditions(fullData);
        setDisplayList(fullData);
      } catch (err) {
        console.error('Trending Roles Fetch Error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadTrendingRoles();
  }, []);

  const handleFilter = (type) => {
    setActiveFilter(type);
    if (type === 'All') {
      setDisplayList(dynamicAuditions);
    } else {
      const filtered = dynamicAuditions.filter((item) => {
        const typeData = (item.auditionType || '').toLowerCase();
        if (type === 'Online') return typeData.includes('online');
        if (type === 'Face-to-Face' || type === 'Walk-In') 
          return typeData.includes('walkin') || typeData.includes('walk-in');
        return false;
      });
      setDisplayList(filtered);
    }
  };

  if (loading) return <div className="text-center py-5"><Spinner variant="danger" /></div>;

  return (
    <section className="trending-roles-section">
      <Container fluid className="trending-roles-container">
        <div className="filter-viewall-row">
          <div className="filter-wrapper d-flex flex-wrap">
            {['All', 'Online', 'Face-to-Face', 'Walk-In'].map((type) => (
              <Button key={type} className={`filter-btn ${activeFilter === type ? 'active' : ''}`} onClick={() => handleFilter(type)}>
                {type}
              </Button>
            ))}
          </div>
          <a href="/roles" className="view-all-inline">View All <ArrowRight size={16} /></a>
        </div>

        <div className="slider-wrapper">
          {displayList.length > 0 ? (
            <Slider {...sliderSettings}>
              {displayList.map((audition) => (
                <div key={audition.id || audition.localRoleId} className="slider-item">
                  <ModernAuditionCard audition={audition} />
                </div>
              ))}
            </Slider>
          ) : (
            <div className="text-center text-white-50 py-4">
              <p>No roles found for this category.</p>
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export default AuditionCategoryRow;